import { getActiveWorkWindow } from './workingHours';
import { getPickupMinTime, getPickupMaxTime } from './pickupWindow';

export type TimeIssue = 'too-soon' | 'too-late' | null;

/**
 * The pickers offer times on a 5-minute grid with the seconds dropped, while
 * the lower bound ("now + an hour", "now + cooking time") slides forward every
 * second. Without a grace of one grid step, the earliest slot the picker
 * itself offers is already too soon by the time it's confirmed.
 */
const GRID_GRACE_MS = 5 * 60 * 1000;

const isBefore = (selected: Date, min: Date) =>
  selected.getTime() < min.getTime() - GRID_GRACE_MS;

interface ValidateScheduledTimeParams {
  isPickup: boolean;
  pickupOn: string | null;
  deliveryOn: string | null;
  deliveryTimeMinutes?: number;
  workTimeFrom?: string | null;
  workTimeTo?: string | null;
  now?: Date;
}

// Re-checked at submit time, not just when the picker was open — the user
// may have picked a time and then sat on the checkout screen long enough for
// it to no longer satisfy the same rules the picker itself enforces.
export function validateScheduledTime({
  isPickup,
  pickupOn,
  deliveryOn,
  deliveryTimeMinutes,
  workTimeFrom,
  workTimeTo,
  now = new Date(),
}: ValidateScheduledTimeParams): TimeIssue {
  if (isPickup) {
    if (!pickupOn) return null;
    const selected = new Date(pickupOn);
    if (isBefore(selected, getPickupMinTime(now))) return 'too-soon';
    if (selected > getPickupMaxTime(now)) return 'too-late';
    return null;
  }

  if (!deliveryOn) return null;
  const selected = new Date(deliveryOn);
  const workWindow = getActiveWorkWindow(now, workTimeFrom, workTimeTo);
  const earliest = new Date(now.getTime() + ((deliveryTimeMinutes || 15) + 30) * 60000);
  const min = workWindow && earliest < workWindow.start ? workWindow.start : earliest;
  if (isBefore(selected, min)) return 'too-soon';
  if (workWindow && selected > workWindow.end) return 'too-late';
  return null;
}
